/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {dlsAlt, dlsColor} from '../theme/theme';
import {DLSTheme} from '../types/types';

export interface WeightSensorIconProps {
  weight: number;
  units: string;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  anchor : {
    position : 'relative'
  },
  weight : {
    ...dlsAlt('typography', 'label3'),
    backgroundColor                : dlsColor('skyBlue'),
    color                          : dlsColor('white'),
    fontWeight                     : 700,
    outline                        : `1px solid ${dlsColor('caBlue')}`,
    padding                        : '4px 6px',
    borderRadius                   : 2,
    position                       : 'absolute',
    top                            : 45,
    left                           : 115,
    [theme.breakpoints.down('sm')] : {
      top  : 60,
      left : 158
    }
  }
}));

/*
 * Defines the Design Language System (DLS) Weight Sensor Icon component.
 */
export const WeightSensorIcon = ({weight, units}: WeightSensorIconProps) => {
  const classes = useStyles();

  return (
    <div className={classes.anchor}>
      <svg width="100%" height="100%" viewBox="0 0 330 130" shapeRendering="geometricPrecision" textRendering="geometricPrecision">
        <g transform="matrix(.96793 0 0 0.96793 6.25948-27.92149)">
          <g mask="url(#eHGMUrEizXD22)">
            <g>
              <path d="M334,39h-340v123h340v-123Z" fill={dlsColor('white')} />
              <path d="M255.538,141.222l1.768-2.471h56.902v-2.472" fill="none" stroke={dlsColor('grey3')} strokeWidth="0.5" />
              <path d="M32.5711,134.746l-1.7494-1.748-13.2454,13.231l1.7494,1.748l13.2454-13.231Z" fill={dlsColor('white')} stroke={dlsColor('grey3')} strokeWidth="0.5" />
              <path d="M19.9682,129.924l-6.1761,3.716v3.345h303.2429v-7.061h-297.0668Z" fill={dlsColor('white')} stroke={dlsColor('grey3')} strokeWidth="0.5" />
              <path d="M60.8396,157.991c7.3198,0,13.2536-5.928,13.2536-13.24s-5.9338-13.239-13.2536-13.239-13.2537,5.927-13.2537,13.239s5.9339,13.24,13.2537,13.24Z" fill={dlsColor('caBlue')} stroke={dlsColor('caBlue')} strokeWidth="0.5" />
              <path d="M60.8398,153.049c4.5871,0,8.3056-3.715,8.3056-8.297s-3.7185-8.297-8.3056-8.297-8.3056,3.715-8.3056,8.297s3.7185,8.297,8.3056,8.297Z" fill={dlsColor('white')} stroke={dlsColor('caBlue')} strokeWidth="0.5" />
              <path d="M90.5281,157.991c7.3197,0,13.2539-5.928,13.2539-13.24s-5.9342-13.239-13.2539-13.239-13.2537,5.927-13.2537,13.239s5.9339,13.24,13.2537,13.24Z" fill={dlsColor('caBlue')} stroke={dlsColor('caBlue')} strokeWidth="0.5" />
              <path d="M90.5283,153.049c4.587,0,8.3056-3.715,8.3056-8.297s-3.7186-8.297-8.3056-8.297c-4.5871,0-8.3056,3.715-8.3056,8.297s3.7185,8.297,8.3056,8.297Z" fill={dlsColor('white')} stroke={dlsColor('caBlue')} strokeWidth="0.5" />
              <path d="M316.859,48.1914h-303.597v77.3186h303.597v-77.3186Z" fill={dlsColor('white')} stroke={dlsColor('grey3')} strokeWidth="0.5" />
              <path d="M224.26,138.574h-53.368v2.472h53.368v-2.472Z" fill={dlsColor('white')} stroke={dlsColor('grey3')} strokeWidth="0.5" />
              <path d="M224.26,142.811h-53.368v2.471h53.368v-2.471Z" fill={dlsColor('white')} stroke={dlsColor('grey3')} strokeWidth="0.5" />
              <path d="M173.719,130.101v17.299h2.474v-17.299h-2.474Z" fill={dlsColor('white')} stroke={dlsColor('grey3')} strokeWidth="0.5" />
              <path d="M218.958,130.101v17.299h2.474v-17.299h-2.474Z" fill={dlsColor('white')} stroke={dlsColor('grey3')} strokeWidth="0.5" />
              <path d="M250.13,138.547l7.121,4.996l1.422-2.022-7.121-4.997-1.422,2.023Z" fill={dlsColor('white')} stroke={dlsColor('grey3')} strokeWidth="0.5" />
              <path d="M254.655,154.107h-6.715v2.472h6.715v-2.472Z" fill={dlsColor('white')} stroke={dlsColor('grey3')} strokeWidth="0.5" />
              <path d="M19.9771,137.867h-2.474v18.712h2.474v-18.712Z" fill={dlsColor('white')} stroke={dlsColor('grey3')} strokeWidth="0.5" />
              <path d="M250.06,134.336v20.83h2.474v-20.83h-2.474Z" fill={dlsColor('white')} stroke={dlsColor('grey3')} strokeWidth="0.5" />
            </g>
            <mask id="eHGMUrEizXD22" style={{maskType: 'alpha'}} x="-150%" y="-150%" height="400%" width="400%">
              <rect width="330" height="185" rx="0" ry="0" fill={dlsColor('grey8')} />
            </mask>
          </g>
          <path d="M63.85,101h26.3L87,79h-20l-3.15,22ZM77,76c.8667,0,1.5833-.2917,2.15-.875s.85-1.2917.85-2.125c0-.8667-.2833-1.5833-.85-2.15s-1.2833-.85-2.15-.85c-.8333,0-1.5417.2833-2.125.85s-.875,1.2833-.875,2.15c0,.8333.2917,1.5417.875,2.125s1.2917.875,2.125.875Zm5.2,0h4.8c.7667,0,1.4333.2417,2,.725s.9,1.1083,1,1.875l3.1,22c.1333.9-.0917,1.692-.675,2.375s-1.3417,1.025-2.275,1.025h-26.3c-.9333,0-1.6917-.342-2.275-1.025s-.8083-1.475-.675-2.375l3.1-22c.1-.7667.4333-1.3917,1-1.875s1.2333-.725,2-.725h4.8c-.2667-.4667-.4667-.9417-.6-1.425s-.2-1.0083-.2-1.575c0-1.6667.5833-3.0833,1.75-4.25s2.5833-1.75,4.25-1.75s3.0833.5833,4.25,1.75s1.75,2.5833,1.75,4.25c0,.5667-.0667,1.0917-.2,1.575s-.3333.9583-.6,1.425ZM63.85,101h26.3-26.3Z" fill={dlsColor('caBlue')} />
          <path d="M76.4697,98.5303c.2929.2929.7677.2929,1.0606,0l4.773-4.7729c.2929-.2929.2929-.7678,0-1.0607s-.7678-.2929-1.0607,0L77,96.9393l-4.2426-4.2426c-.2929-.2929-.7678-.2929-1.0607,0s-.2929.7678,0,1.0607l4.773,4.7729ZM76.25,77v21h1.5v-21h-1.5Z" fill={dlsColor('caBlue')} />
        </g>
      </svg>
      <span className={classes.weight}>
        {translate('common:smarterTrailer.weightSensor.weight', {weight: weight, unit: units})}
      </span>
    </div>
  );
};

export default WeightSensorIcon;
